import { usePublishedHero } from 'hooks/usePublishedHero';
import PageIntroBand from 'components/PageIntroBand';
import { FlowButton } from 'components/ui/flow-button';
import HeroBackground from 'components/HeroBackground';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowUpRight, BookOpen, Loader2 } from 'lucide-react';
import { useReveal } from 'hooks/useReveal';
import BLOG_HERO_CONTENT from 'data/blogPageContent.json';
import HeroScrollRail from 'components/HeroScrollRail';
import SiteHeroCtas, {
  HERO_CTA_SECONDARY_CLASS,
  PAGE_HERO_BODY_CLASS,
  PAGE_HERO_CONTAINER_CLASS,
  PAGE_HERO_CTA_ROW_CLASS,
  PAGE_HERO_EYEBROW_CLASS,
  PAGE_HERO_H1_CLASS,
} from 'components/SiteHeroCtas';
import { listPublishedBlogPosts } from 'lib/blogCms';

const POSTS_PER_PAGE = 12;
const BLOG_HERO_SECTION_CLASS = 'relative flex min-h-[100svh] w-full items-center overflow-hidden md:h-screen md:min-h-[680px]';
const BLOG_MARQUEE_ITEMS = [
  'Driver files',
  'Compliance',
  'Safety follow-up',
  'DataQs',
  'SOPs',
  'Recruiting',
  'Training',
  'TMS workflow',
  'Management visibility',
];

function renderGoldHeroTitle(title) {
  const words = String(title || '').trim().split(/\s+/).filter(Boolean);
  const goldStartIndex = Math.max(0, words.length - 3);
  const regularWords = words.slice(0, goldStartIndex);
  const goldPhrase = words.slice(goldStartIndex).join(' ');

  return (
    <>
      {regularWords.map((word, index) => (
        <span key={`${word}-${index}`} aria-hidden="true" className="mr-3 inline-block overflow-hidden align-bottom sm:mr-4">
          <span className="inline-block">
            {word}
          </span>
        </span>
      ))}
      <span aria-hidden="true" className="mr-3 inline-block overflow-hidden align-bottom sm:mr-4">
        <span className="inline-block italic shimmer-text">
          {goldPhrase}
        </span>
      </span>
    </>
  );
}

const BLOG_CATEGORIES = [
  ['driver-files', 'Driver Files'],
  ['compliance', 'Compliance'],
  ['safety', 'Safety'],
  ['dataqs', 'DataQs'],
  ['sops', 'SOPs'],
  ['recruiting', 'Recruiting'],
  ['training', 'Training'],
  ['tms', 'TMS'],
  ['management', 'Management'],
];

const CATEGORY_ALIASES = {
  'driver-files': ['driver-files', 'dqf', 'driver-qualification-files'],
  compliance: ['compliance', 'new-entrant-audit', 'owner-operators', 'audit-readiness', 'fmcsa'],
  safety: ['safety', 'safety-records', 'safety-meetings', 'corrective-action', 'driver-coaching'],
  dataqs: ['dataqs', 'rdr', 'csa'],
  sops: ['sops', 'sop', 'standard-operating-procedures'],
  recruiting: ['recruiting', 'driver-recruiting'],
  training: ['training', 'driver-training', 'onboarding'],
  tms: ['tms', 'tms-operations', 'workflow', 'dispatch'],
  management: ['management', 'management-control', 'management-visibility', 'dashboards'],
};

function slugify(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function resolveCategoryLabel(categorySlug) {
  const normalized = slugify(categorySlug);
  const canonical = BLOG_CATEGORIES.find(([slug]) => slug === normalized || (CATEGORY_ALIASES[slug] || []).includes(normalized));
  return canonical?.[1] || 'Blog';
}

function resolveCategorySlug(categorySlug) {
  const normalized = slugify(categorySlug);
  if (!normalized) {
    return '';
  }
  const canonical = BLOG_CATEGORIES.find(([slug]) => slug === normalized || (CATEGORY_ALIASES[slug] || []).includes(normalized));
  return canonical?.[0] || normalized;
}

function postMatchesCategory(post, categorySlug) {
  if (!categorySlug) {
    return true;
  }

  const normalizedCategory = slugify(post.category);
  const normalizedTags = Array.isArray(post.tags) ? post.tags.map(slugify) : [];
  const aliases = CATEGORY_ALIASES[categorySlug] || [categorySlug];
  return aliases.some((alias) => normalizedCategory === alias || normalizedTags.includes(alias));
}

function getOneSentenceSummary(post) {
  const summary = String(post.excerpt || post.summary || '').trim();
  if (!summary) {
    return 'Read the full guide for context, examples and suggested next steps.';
  }
  const match = summary.match(/^(.+?[.!?])(\s|$)/);
  return match ? match[1] : summary;
}

function BlogIntroBand() {
  return (
    <PageIntroBand
      testId="blog-page-intro"
      label="Operating notes"
      title="See how the work connects."
      backgroundText="BLOG"
      paragraphs={[
        "A driver’s file can affect a dispatch decision. A repair record supports the decision to return equipment to service. A missing delivery document can hold up billing.",
        "Our guides explain those connections through practical examples and source references. For regulatory topics, check the current requirements and how they apply to your operation.",
      ]}
    />
  );
}

export default function BlogPage() {
  const heroMedia = usePublishedHero('blogLandingHeroMedia');
  const ref = useReveal();
  const hero = BLOG_HERO_CONTENT;
  const heroParagraphs = hero.body.split(/\n{2,}/);
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [articleRequest, setArticleRequest] = useState(0);
  const routeCategory = resolveCategorySlug(category);
  const requestedPage = Number.parseInt(searchParams.get('page') || '1', 10);
  const [selectedCategory, setSelectedCategory] = useState(routeCategory);
  const [selectedPage, setSelectedPage] = useState(Number.isFinite(requestedPage) ? requestedPage : 1);

  const activeCategory = selectedCategory;
  const activeCategoryLabel = resolveCategoryLabel(activeCategory);
  const filteredPosts = useMemo(
    () => posts.filter((post) => postMatchesCategory(post, activeCategory)),
    [activeCategory, posts]
  );

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / POSTS_PER_PAGE));
  const currentPage = Math.min(Math.max(selectedPage, 1), totalPages);
  const paginatedPosts = useMemo(() => {
    const start = (currentPage - 1) * POSTS_PER_PAGE;
    return filteredPosts.slice(start, start + POSTS_PER_PAGE);
  }, [currentPage, filteredPosts]);
  const featuredPost = paginatedPosts[0] || null;
  const latestPosts = paginatedPosts.slice(1);
  const activeCategoryCopy = hero.categories[activeCategory];
  const heroTitle = activeCategory ? activeCategoryCopy?.title || `${activeCategoryLabel} articles for carrier teams.` : hero.title;

  const handleCategorySelect = (categorySlug = '') => {
    setSelectedCategory(categorySlug);
    setSelectedPage(1);
  };

  useEffect(() => {
    setSelectedCategory(routeCategory);
    setSelectedPage(Number.isFinite(requestedPage) ? requestedPage : 1);
  }, [routeCategory, requestedPage]);

  useEffect(() => {
    let mounted = true;
    setStatus('loading');
    setError('');

    listPublishedBlogPosts()
      .then((loadedPosts) => {
        if (!mounted) return;
        setPosts(loadedPosts);
        setStatus('ready');
      })
      .catch((loadError) => {
        if (!mounted) return;
        setError(loadError?.message || 'The articles could not load. Please try again.');
        setStatus('error');
      });

    return () => {
      mounted = false;
    };
  }, [articleRequest]);

  return (
    <div ref={ref} className="min-h-screen bg-canvas">
      <section data-theme-surface="dark" className={`${BLOG_HERO_SECTION_CLASS} site-image-hero`}>
        <HeroBackground imageUrl={heroMedia.imageUrl} imageAlt={heroMedia.imageAlt} />
        <div className="absolute inset-0 grain-overlay" />
        <div data-hero-text-parallax="true" className={PAGE_HERO_CONTAINER_CLASS}>
          <div className="w-full max-w-[342px] sm:max-w-3xl lg:max-w-[580px]">
            <p className={`${PAGE_HERO_EYEBROW_CLASS} visible`}>{hero.eyebrow}</p>
            <h1 aria-label={heroTitle} className={`${PAGE_HERO_H1_CLASS} visible`}>
              {renderGoldHeroTitle(heroTitle)}
            </h1>
            <div className={`${PAGE_HERO_BODY_CLASS} visible`}>
              {(activeCategory ? [activeCategoryCopy?.body || heroParagraphs[0]].filter(Boolean) : heroParagraphs).map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
            {!activeCategory ? <SiteHeroCtas content={hero} className={PAGE_HERO_CTA_ROW_CLASS} /> : null}
          </div>
        </div>
        <HeroScrollRail targetId="articles" />
      </section>

      <div className="topic-rail overflow-hidden border-y border-ink/10 bg-surface">
        <div className="marquee-container">
          <div className="marquee-track">
            {[...BLOG_MARQUEE_ITEMS, ...BLOG_MARQUEE_ITEMS].map((item, index) => (
              <span key={`${item}-${index}`} className="inline-flex items-center gap-3 px-9 font-body text-[9px] uppercase tracking-[0.44em] text-copy/24 whitespace-nowrap">
                {item}
                <span className="h-1 w-1 rotate-45 bg-gold/45" />
              </span>
            ))}
          </div>
        </div>
      </div>

      <BlogIntroBand />

      <section id="articles" className="bg-surface border-y border-ink/10">
        <div className="site-content-shell py-12 md:py-14">
          <div className="mx-auto max-w-4xl text-center">
            <h2 className="reveal reveal-delay-1 font-display text-3xl sm:text-4xl tracking-tight text-ink mb-5">
              Find your area of work.
            </h2>
            <div className="mx-auto max-w-3xl space-y-4 font-body text-copy/52 leading-relaxed">
              <p>Written for US carrier owners, office teams and drivers who want to understand the records, decisions and responsibilities behind their work.</p>
              <p>Choose a subject below to narrow the library. Use Driver Files for qualification records, DataQs for reviews of safety data, or Management for operating decisions.</p>
            </div>
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-x-8 gap-y-4 border-t border-ink/10 pt-7">
            <button
              type="button"
              onClick={() => handleCategorySelect('')}
              className={`border-b pb-2 text-[11px] uppercase tracking-[0.22em] font-body transition-colors ${!activeCategory ? 'border-gold text-accent-ink' : 'border-transparent text-copy/48 hover:border-gold/40 hover:text-accent-ink'}`}
            >
              All
            </button>
            {BLOG_CATEGORIES.map(([slug, label]) => (
              <button
                key={slug}
                type="button"
                onClick={() => handleCategorySelect(slug)}
                className={`border-b pb-2 text-[11px] uppercase tracking-[0.22em] font-body transition-colors ${activeCategory === slug ? 'border-gold text-accent-ink' : 'border-transparent text-copy/48 hover:border-gold/40 hover:text-accent-ink'}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-surface">
        <div className="site-content-shell">
          {status === 'loading' ? (
            <div className="flex items-center gap-3 text-copy/50">
              <Loader2 className="w-5 h-5 animate-spin text-accent-ink" />
              <span className="font-body text-sm">Loading articles…</span>
            </div>
          ) : status === 'error' ? (
            <div className="font-body text-base leading-7 text-ink">
              <p role="status" className="max-w-[65ch]">{error}</p>
              <div className="site-hero-ctas mt-6 flex flex-wrap gap-4">
                <FlowButton onClick={() => setArticleRequest(current => current + 1)}>Try again</FlowButton>
                <FlowButton to="/#contact">Contact Magellan</FlowButton>
              </div>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="border border-ink/8 bg-canvas p-10 text-center max-w-2xl mx-auto">
              <BookOpen className="w-10 h-10 text-accent-ink mx-auto mb-5" strokeWidth={1.5} />
              <h2 className="font-display text-3xl text-ink mb-4">No articles in this view yet.</h2>
              <p className="font-body text-copy/45 leading-relaxed">
                Choose another subject or return to All to see every published guide.
              </p>
              <p className="font-body text-copy/45 leading-relaxed mt-4 mb-8">
                You can also explore the resource library.
              </p>
              <FlowButton to="/resources" className={HERO_CTA_SECONDARY_CLASS}>
                Explore resources
              </FlowButton>
            </div>
          ) : (
            <div className="space-y-10">
              <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-accent-ink font-body mb-3">Article library</p>
                  <h2 className="font-display text-3xl text-ink tracking-tight">{activeCategory ? activeCategoryLabel : 'Latest articles'}</h2>
                  <p className="mt-3 font-body text-copy/42">Worked examples and practical steps for everyday carrier tasks.</p>
                </div>
                <div className="text-left md:text-right font-body text-sm text-copy/42">
                  <p className="font-body text-sm text-copy/42">Showing {paginatedPosts.length} of {filteredPosts.length} articles</p>
                </div>
              </div>

              <div className="grid auto-rows-[280px] grid-cols-1 gap-5 md:grid-cols-3 lg:gap-6">
                {[featuredPost, ...latestPosts].filter(Boolean).map((post, index) => (
                  <Link data-theme-surface="dark"
                    key={post.id || post.slug}
                    to={`/blog/${post.slug}`}
                    className={`group relative overflow-hidden rounded-xl border border-ink/10 cursor-pointer ${index === 0 ? 'md:col-span-2 md:row-span-2' : ''}`}
                  >
                    {post.featuredImageUrl ? (
                      <img
                        src={post.featuredImageUrl}
                        alt={post.featuredImageAlt || post.title}
                        className="absolute inset-0 h-full w-full object-cover opacity-60 transition-all duration-1000 ease-out group-hover:scale-110 group-hover:opacity-80"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-gold/18 via-white/[0.04] to-black" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/45 to-transparent" />
                    <div className="relative z-10 flex h-full flex-col justify-between p-7">
                      <div className="flex items-center justify-between gap-4">
                        <span className="rounded-full border border-gold/30 px-3 py-1 font-body text-[10px] uppercase tracking-[0.24em] text-accent-ink">
                          {post.category || 'Blog'}
                        </span>
                        <ArrowUpRight className="h-5 w-5 text-copy/40 transition-all duration-500 group-hover:rotate-45 group-hover:text-ink" />
                      </div>
                      <div>
                        <h2 className={`font-display leading-tight text-ink ${index === 0 ? 'text-3xl lg:text-5xl' : 'text-2xl'}`}>
                          {post.title}
                        </h2>
                        <p className="mt-3 max-w-xl font-body text-sm leading-6 text-tone-300">{getOneSentenceSummary(post)}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {!latestPosts.length && featuredPost ? (
                <div className="border-y border-ink/10 py-8">
                  <p className="font-body text-copy/42">Choose another subject to explore the rest of the library.</p>
                </div>
              ) : null}

              {totalPages > 1 ? (
                <nav className="mt-12 flex flex-wrap items-center justify-center gap-3" aria-label="Blog pagination">
                  <button
                    type="button"
                    onClick={() => setSelectedPage((page) => Math.max(1, page - 1))}
                    aria-disabled={currentPage === 1}
                    className={`border px-5 py-3 text-[10px] uppercase tracking-[0.2em] font-body transition-colors ${currentPage === 1 ? 'pointer-events-none border-ink/8 text-ink/20' : 'border-ink/15 text-copy/60 hover:border-gold/40 hover:text-accent-ink'}`}
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setSelectedPage(page)}
                      aria-current={page === currentPage ? 'page' : undefined}
                      className={`min-w-11 border px-4 py-3 text-center text-[10px] uppercase tracking-[0.2em] font-body transition-colors ${page === currentPage ? 'border-gold bg-gold text-obsidian' : 'border-ink/15 text-copy/60 hover:border-gold/40 hover:text-accent-ink'}`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setSelectedPage((page) => Math.min(totalPages, page + 1))}
                    aria-disabled={currentPage === totalPages}
                    className={`border px-5 py-3 text-[10px] uppercase tracking-[0.2em] font-body transition-colors ${currentPage === totalPages ? 'pointer-events-none border-ink/8 text-ink/20' : 'border-ink/15 text-copy/60 hover:border-gold/40 hover:text-accent-ink'}`}
                  >
                    Next
                  </button>
                </nav>
              ) : null}
            </div>
          )}
        </div>
      </section>

      <section data-theme-surface="dark" className="relative min-h-[220px] overflow-hidden flex items-center">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(/images/resources/resources-road-truck.png)' }} aria-hidden="true" />
        <div className="absolute inset-0 bg-canvas/72" />
        <div className="absolute inset-0 bg-gradient-to-r from-canvas via-canvas/58 to-canvas/30" />
        <div className="site-content-shell relative z-10 py-12 text-center">
          <p className="mx-auto max-w-4xl font-display text-3xl sm:text-4xl text-accent-ink leading-tight">
            Choose one issue to work through.
          </p>
          <p className="mx-auto mt-6 max-w-2xl font-body text-copy/52 leading-relaxed">
            Read the relevant guide alongside your records. Note any missing information and the question or action you need to follow up.
          </p>
        </div>
      </section>

    </div>
  );
}
